import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditProductHelpModal from 'components/admin/common/modal/product/EditProductHelpModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const ProductHelp = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createProductHelpData,
        postError: creatingError,
        postLoading: creating,
        putData: updateProductHelpData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteProductHelpData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PRODUCT_HELP);

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteProductHelp = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditProductHelp = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteProductHelp, modalData: productHelpToDelete, openModal: openDeleteProductHelp, closeModal: closedDeleteProductHelp } = useModalState({
        onClose: handleCloseDeleteProductHelp
    });

    const { isOpen: isOpenEditProductHelp, modalData: selectedProductHelp, openModal: openEditProductHelp, closeModal: closedEditProductHelp } = useModalState({
        onClose: handleCloseEditProductHelp
    });

    const validationRules = [
        { field: 'title', method: 'required' },
        { field: 'description', method: 'required' },
        { field: 'video_id', method: ['required', 'validUrl'] }
    ];

    const handleModalSave = useCallback(async (updatedProductHelp) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedProductHelp, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedProductHelp.id) {
                await updateProductHelpData(updatedProductHelp.id, updatedProductHelp, true);
                setSuccessMessage("Videos de Ayuda actualizada con éxito.");
            } else {
                await createProductHelpData(updatedProductHelp, true);
                setSuccessMessage("Videos de Ayuda creada con éxito.");
            }
            closedEditProductHelp();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateProductHelpData, createProductHelpData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newProductHelp = {
            title: '',
            description: '',
            video_id: ''
        };
        openEditProductHelp(newProductHelp);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (productHelpToDelete) {
            try {
                await deleteProductHelpData(productHelpToDelete.id);
                setSuccessMessage("Videos de Ayuda eliminada con éxito.");
                refetch();
                closedDeleteProductHelp();
            } catch (error) {
                console.error(error);
            }
        }
    }, [productHelpToDelete, deleteProductHelpData, refetch]);

    const columns = useMemo(() => [
        { field: 'title', header: 'Título', sortable: true },
        { field: 'description', header: 'Descripción', sortable: true },
        { field: 'video_id', header: 'ID del Video', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Videos de Ayuda</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Videos de Ayuda
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditProductHelp}
                onDelete={openDeleteProductHelp}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProductHelpModal
                show={isOpenEditProductHelp}
                productHelp={selectedProductHelp}
                handleClose={closedEditProductHelp}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteProductHelp}
                onHide={closedDeleteProductHelp}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(ProductHelp);